/**
 * @fileoverview Componente responsável por criar e editar tags.
 */

import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { ColorBox } from 'material-ui-color';
import { Plus, Pencil, Pipette, X } from "lucide-react";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { IconButton, InputAdornment } from "@material-ui/core";

const useStyles = makeStyles(theme => {
	const dark = theme.palette.type === "dark";
	return {
		root: { display: "flex", flexWrap: "wrap" },
		dialog: {
			"& .MuiDialog-paper": {
				fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
				width: "min(480px, calc(100vw - 32px))",
				maxWidth: "calc(100vw - 32px)",
				borderRadius: 24,
				overflow: "hidden",
				background: dark
					? "linear-gradient(180deg, #171a20 0%, #111319 100%)"
					: "#ffffff",
				boxShadow: dark
					? "0 24px 80px rgba(0, 0, 0, 0.45)"
					: "0 24px 60px rgba(15, 23, 42, 0.12)",
				border: dark
					? "1px solid rgba(255, 255, 255, 0.08)"
					: "1px solid rgba(15, 23, 42, 0.08)",
			},
			"& .MuiBackdrop-root": {
				backgroundColor: dark ? "rgba(0, 0, 0, 0.7)" : "rgba(0, 0, 0, 0.4)",
			},
		},
		modalHeader: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			padding: theme.spacing(2.5, 3),
			borderBottom: dark
				? "1px solid rgba(255, 255, 255, 0.06)"
				: "1px solid rgba(15, 23, 42, 0.08)",
		},
		modalTitle: {
			fontSize: "1.35rem",
			fontWeight: 700,
			color: dark ? "#f8fafc" : "#111827",
		},
		modalClose: {
			color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
		},
		multFieldLine: {
			display: "flex",
			"& > *:not(:last-child)": { marginRight: theme.spacing(1) },
		},
		textField: {
			"& .MuiOutlinedInput-root": {
				borderRadius: "14px",
				background: dark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
				border: dark
					? "1px solid rgba(91, 124, 255, 0.15)"
					: "1px solid rgba(0, 0, 0, 0.08)",
				transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
				"& fieldset": { borderColor: "transparent" },
				"&:hover": {
					background: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(61, 92, 253, 0.04)",
					border: dark
						? "1px solid rgba(91, 124, 255, 0.25)"
						: "1px solid rgba(61, 92, 253, 0.15)",
				},
				"&.Mui-focused": {
					background: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(255, 255, 255, 1)",
					border: dark
						? "1px solid rgba(91, 124, 255, 0.4)"
						: "1px solid rgba(61, 92, 253, 0.3)",
					boxShadow: "0 0 0 4px rgba(91, 124, 255, 0.15), 0 8px 24px rgba(91, 124, 255, 0.2)",
				},
			},
		},
		colorAdorment: {
			width: 22,
			height: 22,
			borderRadius: 6,
			border: dark
				? "1px solid rgba(255, 255, 255, 0.18)"
				: "1px solid rgba(15, 23, 42, 0.18)",
		},

		// ========= Footer =========
		dialogActions: {
			padding: theme.spacing(2, 3),
			borderTop: dark
				? "1px solid rgba(255, 255, 255, 0.06)"
				: "1px solid rgba(15, 23, 42, 0.08)",
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(1),
		},
		cancelLeft: { marginRight: "auto" },

		btnBase: {
			height: 38,
			padding: "0 16px",
			borderRadius: 10,
			fontSize: "0.875rem",
			fontWeight: 500,
			textTransform: "none",
			whiteSpace: "nowrap",
			flexShrink: 0,
			display: "inline-flex",
			alignItems: "center",
			gap: 8,
			border: "none",
			boxShadow: "none",
			transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
			"& svg": { flexShrink: 0 },
			"& .MuiButton-label": { whiteSpace: "nowrap" },
		},

		btnCancel: {
			background: "transparent",
			border: dark
				? "1px solid rgba(255, 255, 255, 0.12)"
				: "1px solid rgba(15, 23, 42, 0.12)",
			color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
			"&:hover": {
				background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
				color: dark ? "#f8fafc" : "#111827",
			},
		},

		btnPrimary: {
			background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
			color: "#ffffff",
			"&:hover": {
				background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
			},
			"&:disabled": { opacity: 0.55, cursor: "not-allowed" },
		},

		spinner: { color: "#ffffff" },
	};
});

const TagSchema = Yup.object().shape({
	name: Yup.string()
		.min(3, "Mensagem muito curta")
		.required("Obrigatório")
});

const INITIAL_TAG_STATE = {
	name: "",
	color: ""
};

const TagModal = ({ open, onClose, tagId, reload }) => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);

	const [tag, setTag] = useState({ ...INITIAL_TAG_STATE });

	useEffect(() => {
		if (!open) return;

		let isMounted = true;

		const loadTag = async () => {
			if (!tagId) {
				setTag({ ...INITIAL_TAG_STATE });
				return;
			}

			try {
				const { data } = await api.get(`/tags/${tagId}`);
				if (!isMounted) return;
				setTag({ ...INITIAL_TAG_STATE, ...data });
			} catch (err) {
				if (!isMounted) return;
				toastError(err);
			}
		};

		loadTag();

		return () => {
			isMounted = false;
		};
	}, [tagId, open]);

	const handleClose = () => {
		setTag({ ...INITIAL_TAG_STATE });
		setColorPickerModalOpen(false);
		onClose();
	};

	const handleSaveTag = async values => {
		const tagData = { ...values, userId: user.id };
		try {
			if (tagId) {
				await api.put(`/tags/${tagId}`, tagData);
			} else {
				await api.post("/tags", tagData);
			}
			toast.success(i18n.t("tagModal.success"));
			if (typeof reload == 'function') {
				reload();
			}
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="paper"
				className={classes.dialog}
			>
				<div className={classes.modalHeader}>
					<Typography className={classes.modalTitle}>
						{tagId ? i18n.t("tagModal.title.edit") : i18n.t("tagModal.title.add")}
					</Typography>
					<IconButton size="small" onClick={handleClose} className={classes.modalClose}>
						<X size={18} strokeWidth={1.75} />
					</IconButton>
				</div>
				<Formik
					initialValues={tag}
					enableReinitialize={true}
					validationSchema={TagSchema}
					validateOnChange={false}
					validateOnBlur={true}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveTag(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("tagModal.form.name")}
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										onChange={(e) => setTag(prev => ({ ...prev, name: e.target.value }))}
										fullWidth
										className={classes.textField}
									/>
								</div>
								<br />
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										fullWidth
										label={i18n.t("tagModal.form.color")}
										name="color"
										id="color"
										error={touched.color && Boolean(errors.color)}
										helperText={touched.color && errors.color}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<div
														style={{ backgroundColor: values.color }}
														className={classes.colorAdorment}
													></div>
												</InputAdornment>
											),
											endAdornment: (
												<IconButton
													size="small"
													onClick={() => setColorPickerModalOpen(!colorPickerModalOpen)}
												>
													<Pipette size={18} strokeWidth={1.75} />
												</IconButton>
											),
										}}
										variant="outlined"
										margin="dense"
										className={classes.textField}
									/>
								</div>
									{colorPickerModalOpen && (
									<div>
										<ColorBox
											disableAlpha={true}
											hslGradient={false}
											style={{ margin: '20px auto 0' }}
											value={tag.color}
											onChange={val => {
												setTag(prev => ({ ...prev, color: `#${val.hex}` }));
											}}
										/>
									</div>
								)}
							</DialogContent>
							<DialogActions className={classes.dialogActions}>
								<Button
									onClick={handleClose}
									disabled={isSubmitting}
									className={`${classes.btnBase} ${classes.btnCancel} ${classes.cancelLeft}`}
									startIcon={<X size={16} strokeWidth={1.75} />}
								>
									{i18n.t("tagModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									disabled={isSubmitting}
									className={`${classes.btnBase} ${classes.btnPrimary}`}
									startIcon={
										isSubmitting ? (
											<CircularProgress size={14} className={classes.spinner} />
										) : tagId ? (
											<Pencil size={16} strokeWidth={1.75} />
										) : (
											<Plus size={16} strokeWidth={1.75} />
										)
									}
								>
									{tagId
										? i18n.t("tagModal.buttons.okEdit")
										: i18n.t("tagModal.buttons.okAdd")}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default TagModal;
